import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { mangas } from '@/mocks/data';

export default function MangaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const manga = mangas.find((m) => m.id === id);
  const [chapterSort, setChapterSort] = useState<'asc' | 'desc'>('desc');

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <Header />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const sortedChapters = [...manga.chapters].sort((a, b) =>
    chapterSort === 'desc' ? b.number - a.number : a.number - b.number
  );

  const categoryLabel = ['qihuan' as const, 'lianai' as const, 'wuxia' as const, 'xuanyi' as const, 'kehuan' as const, 'gaoxiao' as const, 'kongbu' as const, 'rexue' as const].find(
    (c) => c === manga.category
  ) || 'qihuan';
  const categoryMap: Record<string, string> = {
    qihuan: '奇幻',
    lianai: '恋爱',
    wuxia: '武侠',
    xuanyi: '悬疑',
    kehuan: '科幻',
    gaoxiao: '搞笑',
    kongbu: '恐怖',
    rexue: '热血',
  };
  const categoryName = categoryMap[categoryLabel] || '奇幻';

  const relatedMangas = mangas.filter((m) => m.category === manga.category && m.id !== manga.id).slice(0, 4);

  const handleChapterClick = (chapterNumber: number) => {
    navigate(`/manga/${manga.id}?chapter=${chapterNumber}`);
  };

  return (
    <>
      <ScrollToTop />
      <Header />

      <main className="pt-20">
        <div className="bg-background-100 py-6">
          <div className="max-w-screen-2xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${categoryName}漫画`, href: `/category/${manga.category}` },
                { label: manga.title },
              ]}
            />
          </div>
        </div>

        <article className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={manga.cover}
                    alt={`${manga.title}漫画封面 - 在线免费阅读`}
                    title={`${manga.title} - 178漫画网`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">{manga.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">作者：{manga.author}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${manga.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'}`}>
                    {manga.status}
                  </span>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < Math.floor(manga.rating) ? 'ri-star-fill text-accent-500' : i < manga.rating ? 'ri-star-half-fill text-accent-500' : 'ri-star-fill text-background-300'}`}
                      ></i>
                    ))}
                    <span className="text-sm font-semibold text-foreground-900 ml-1">{manga.rating.toFixed(1)}</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6">
                  <span>
                    <i className="ri-eye-line mr-1"></i>
                    {manga.views > 10000 ? `${(manga.views / 10000).toFixed(0)}万` : manga.views} 阅读
                  </span>
                  <span>
                    <i className="ri-file-list-3-line mr-1"></i>
                    共 {manga.chapters.length} 话
                  </span>
                  <span>
                    <i className="ri-calendar-line mr-1"></i>
                    更新于 {manga.updatedAt}
                  </span>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">{manga.description}</p>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleChapterClick(1)}
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line mr-1.5"></i>
                    从第一话开始
                  </button>
                  <button
                    onClick={() => handleChapterClick(manga.chapters.length)}
                    className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-skip-forward-line mr-1.5"></i>
                    阅读最新话
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-100">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900">章节列表</h2>
              <button
                onClick={() => setChapterSort(chapterSort === 'desc' ? 'asc' : 'desc')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 text-foreground-600 text-sm hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={`text-sm ${chapterSort === 'desc' ? 'ri-sort-desc' : 'ri-sort-asc'}`}></i>
                {chapterSort === 'desc' ? '从新到旧' : '从旧到新'}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {sortedChapters.map((chapter) => (
                <button
                  key={chapter.id}
                  onClick={() => handleChapterClick(chapter.number)}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-background-200/70 transition-colors text-left whitespace-nowrap cursor-pointer"
                >
                  <span className="text-sm font-medium text-foreground-800 truncate">
                    第{chapter.number}话：{chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 ml-2 flex-shrink-0">{chapter.pages}页</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {relatedMangas.length > 0 && (
          <section className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
            <div className="max-w-screen-2xl mx-auto">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-6">同类推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {relatedMangas.map((rm) => (
                  <Link
                    key={rm.id}
                    to={`/manga/${rm.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={rm.cover}
                        alt={`${rm.title}漫画`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <p className="text-white text-xs font-medium">{rm.rating.toFixed(1)} 分</p>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                      {rm.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${manga.title} - 在线免费阅读漫画全集`,
          description: manga.description,
          author: {
            '@type': 'Person',
            name: manga.author,
          },
          datePublished: manga.chapters[0].updatedAt,
          dateModified: manga.updatedAt,
          publisher: {
            '@type': 'Organization',
            name: '洞洞杂货铺漫画',
          },
          url: `https://www.178mh.com/manga/${manga.id}`,
          image: manga.cover,
        })}
      </script>
    </>
  );
}